import { type FormEvent, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  type AdminUser,
  useAdminCreateUser,
  useAdminOrgs,
} from '@/lib/admin';

export function CreateUserPanel() {
  const orgs = useAdminOrgs();
  const create = useAdminCreateUser();

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [orgId, setOrgId] = useState<string>('');
  const [orgRole, setOrgRole] = useState('member');
  const [grantAdmin, setGrantAdmin] = useState(false);

  const [generated, setGenerated] = useState<string | null>(null);
  const [createdUser, setCreatedUser] = useState<AdminUser | null>(null);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!email.trim()) return;
    try {
      const result = await create.mutateAsync({
        email: email.trim(),
        password: password.trim() || undefined,
        name: name.trim() || undefined,
        organization_id: orgId === '' ? null : Number(orgId),
        organization_role: orgRole,
        grant_admin_role: grantAdmin,
      });
      setGenerated(result.generated_password);
      setCreatedUser(result.user);
      setEmail('');
      setName('');
      setPassword('');
      setOrgId('');
      setOrgRole('member');
      setGrantAdmin(false);
    } catch {
      /* surfaced below */
    }
  };

  // Hide solo orgs (member_count < 2) from the dropdown so the admin
  // doesn't accidentally drop a brand-new user into someone else's
  // starter workshop. Once an owner invites a teammate the org is
  // selectable here.
  const sharedOrgs = orgs.data?.filter((o) => o.member_count >= 2) ?? [];

  return (
    <section className="rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 p-5">
      <h2 className="text-lg font-semibold">Create user</h2>
      <p className="mt-1 text-sm text-stone-600 dark:text-stone-400">
        Bypasses self-registration. Leave password blank to have the server
        generate one — you'll see it once on this screen.
      </p>

      <form onSubmit={onSubmit} className="mt-4 space-y-3">
        <div className="grid gap-3 md:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-stone-600 dark:text-stone-400">
              Email
            </span>
            <Input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-stone-600 dark:text-stone-400">
              Display name (optional)
            </span>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-stone-600 dark:text-stone-400">
              Password (optional — leave blank to auto-generate)
            </span>
            <Input
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={password ? 8 : undefined}
            />
          </label>
          <div className="grid-cols-[2fr,1fr] gap-2">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-stone-600 dark:text-stone-400">
                Add to organization
              </span>
              <select
                value={orgId}
                onChange={(e) => setOrgId(e.target.value)}
                className="block w-full rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-3 py-2 text-sm"
              >
                <option value="">— none —</option>
                {sharedOrgs.map((o) => (
                  <option key={o.id} value={String(o.id)}>
                    {o.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-stone-600 dark:text-stone-400">
                Role
              </span>
              <select
                value={orgRole}
                onChange={(e) => setOrgRole(e.target.value)}
                disabled={orgId === ''}
                className="block w-full rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-3 py-2 text-sm disabled:bg-stone-50 disabled:text-stone-400"
              >
                <option value="member">member</option>
                <option value="admin">admin</option>
                <option value="owner">owner</option>
                <option value="viewer">viewer</option>
              </select>
            </label>
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={grantAdmin}
            onChange={(e) => setGrantAdmin(e.target.checked)}
            className="h-4 w-4 rounded border border-stone-300 dark:border-stone-700 text-forge-600 focus:ring-forge-500"
          />
          <span>Grant system <code className="font-mono">admin</code> role</span>
        </label>

        {create.isError && (
          <p className="text-sm text-red-600">{(create.error as Error).message}</p>
        )}

        <div className="flex justify-end">
          <Button type="submit" disabled={create.isPending || !email.trim()}>
            {create.isPending ? 'Creating…' : 'Create user'}
          </Button>
        </div>
      </form>

      {createdUser && (
        <div className="mt-4 rounded-md border border-green-200 bg-green-50 p-3 text-sm">
          <p className="font-medium text-green-800">
            Created {createdUser.email} (id {createdUser.id})
          </p>
          {generated && (
            <p className="mt-1 text-green-800">
              Generated password (shown once):{' '}
              <code className="rounded bg-white dark:bg-stone-900 px-1.5 py-0.5 font-mono text-stone-900 dark:text-stone-100">
                {generated}
              </code>
              <button
                type="button"
                onClick={() => navigator.clipboard?.writeText(generated)}
                className="ml-2 text-xs text-green-700 underline"
              >
                copy
              </button>
            </p>
          )}
        </div>
      )}
    </section>
  );
}
